import { useTheme, Box, Typography, Paper, Chip } from '@mui/material';
import { capitalizeFirstLetter } from 'state/helpers';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';


const formatCellValue = (value) => {
    if (value == null) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return Number.isInteger(value) ? value : parseFloat(value).toFixed(2);
    if (Array.isArray(value)) return value.join(', ') || '—';
    return String(value);
};

const PivotCard = ({ row, index, months, canView, onViewClick }) => {
    const theme = useTheme();
    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Box
                    flex={1}
                    minWidth={0}
                    sx={{
                        color: row.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
                        cursor: canView ? 'pointer' : 'default',
                        '&:hover': canView ? { textDecoration: 'underline' } : {},
                    }}
                    onClick={() => { if (canView) onViewClick(row); }}
                >
                    <Typography 
                        variant="body2" 
                        noWrap
                    >
                        {row.crewCode} {row.crewName}
                    </Typography>
                </Box>
                <Typography variant="body2" color="primary" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                    {parseFloat(row.totalHours || 0).toFixed(2)}
                </Typography>
            </Box>

            <FlexBetween mt={0.75}>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                    {row.post && (
                        <Chip 
                            label={row.post} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                    )}
                    {row.base && (
                        <Chip 
                            label={row.base} 
                            size="small" 
                            variant="outlined" 
                            sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Avg: {parseFloat(row.avgHours || 0).toFixed(2)}
                </Typography>
            </FlexBetween>

            {months.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    {months.map(m => `${m}: ${parseFloat(row[m] || 0).toFixed(2)}`).join(' · ')}
                </Typography>
            )}
        </Paper>
    );
};

const STATS_SKIP_KEYS = new Set(['_id', 'crewCode', 'crewName', 'gender']);

const StatsCard = ({ row, index, canView, onViewClick }) => {
    const theme = useTheme();
    const entries = Object.entries(row).filter(([k]) => !STATS_SKIP_KEYS.has(k));

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Box
                    flex={1}
                    minWidth={0}
                    sx={{
                        color: row.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
                        cursor: canView ? 'pointer' : 'default',
                        '&:hover': canView ? { textDecoration: 'underline' } : {},
                    }}
                    onClick={() => { if (canView) onViewClick(row); }}
                >
                    <Typography 
                        variant="body2" 
                        noWrap
                    >
                        {row.crewCode} {row.crewName}
                    </Typography>
                </Box>
            </Box>

            {entries.map(([k, v]) => (
                <FlexBetween 
                    key={k} 
                    py={0.2}
                >
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                    >
                        {capitalizeFirstLetter(k)}
                    </Typography>
                    <Typography 
                        variant="caption" 
                        fontWeight="medium"
                    >
                        {formatCellValue(v)}
                    </Typography>
                </FlexBetween>
            ))}
        </Paper>
    );
};


const RosterAccumulatesList = ({ rows, isStats, months, canView, onViewClick }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) =>
                isStats ? (
                    <StatsCard key={row._id ?? index} row={row} index={index} canView={canView} onViewClick={onViewClick} />
                ) : (
                    <PivotCard key={row._id ?? index} row={row} index={index} months={months} canView={canView} onViewClick={onViewClick} />
                )
            )}
        </Box>
    );
};

export default RosterAccumulatesList;
